/**
 * Real testimonials for the Hiring Manager Portfolio Review.
 * Photos fetched via HarvestAPI from each person's LinkedIn and re-hosted under
 * /public/testimonials/pr/. Cards fall back to initials when a photo is absent
 * (Some earlier entries had no public LinkedIn avatar and fell back to initials).
 *
 * NOTE: Raj Shah's LinkedIn slug intentionally ends with a hyphen
 * (/in/raj-shah-/) — that is his actual vanity URL, not a truncation.
 * Verified in a browser 2026-09-06: LinkedIn keeps that exact slug in the
 * address bar and it resolves to his profile (Senior Product Designer).
 */

export type PRTestimonial = {
  quote: string;
  name: string;
  role: string;
  /** Current company (curated from LinkedIn). Omitted when not a real brand. */
  company?: string;
  initials: string;
  linkedinUrl: string;
  /** Headshot at /public/testimonials/pr/<slug>.jpg. Initials fallback if absent. */
  photo?: string;
};

export const PR_TESTIMONIALS: PRTestimonial[] = [
  {
    quote:
      "The report helps me see my portfolio the way a hiring manager would, someone overloaded with portfolios and deciding fast. Very helpful, and it gave me clear things to work on.",
    name: "Raj Shah",
    role: "Senior Product Designer",
    company: "Stealth Startup",
    initials: "RS",
    linkedinUrl: "https://www.linkedin.com/in/raj-shah-/",
    photo: "/testimonials/pr/raj-shah.jpg",
  },
  {
    quote:
      "The report is detailed and accurate. Insightful feedback and a solid document that pushed me to do better on the next version of my portfolio.",
    name: "Raphaël Digout",
    role: "Senior Product Designer",
    company: "OODA AI",
    initials: "RD",
    linkedinUrl: "https://www.linkedin.com/in/raphael-digout",
    photo: "/testimonials/pr/raphael-digout.jpg",
  },
  {
    quote:
      "Some of the most useful feedback I've gotten on my portfolio. It hit the right spots, and I really appreciated the assessment.",
    name: "Younes Hadry",
    role: "Product Design Leader",
    company: "Velora",
    initials: "YH",
    linkedinUrl: "https://www.linkedin.com/in/younes-hadry-904a2524/",
    photo: "/testimonials/pr/younes-hadry.jpg",
  },
  {
    quote:
      "It's always good to get an external perspective. The review was super comprehensive and detailed, honestly impressive.",
    name: "Christian Lafrance",
    role: "Sr Staff Product Designer",
    company: "Intuit",
    initials: "CL",
    linkedinUrl: "https://www.linkedin.com/in/madeinlafrance",
    photo: "/testimonials/pr/christian-lafrance.jpg",
  },
  {
    quote:
      "It helped a lot. Clear, practical feedback I could act on right away, exactly what I needed from a portfolio review.",
    name: "Ana D.",
    role: "Senior Product Designer",
    initials: "AD",
    linkedinUrl: "https://www.linkedin.com/in/anadocioherrero",
    photo: "/testimonials/pr/ana-docio-herrero.jpg",
  },
  {
    quote:
      "This was amazing. I really appreciated the time and the report, super interesting and useful for how I present my work.",
    name: "Débora Edelberg",
    role: "Senior Product Designer",
    company: "itsdeb",
    initials: "DE",
    linkedinUrl: "https://www.linkedin.com/in/deboraedelberg",
    photo: "/testimonials/pr/debora-edelberg.jpg",
  },
  {
    quote:
      "Appreciate the portfolio review. Clear feedback and a useful outside read on the work, thank you for making time for this.",
    name: "Monica Porucznik",
    role: "Senior Product Designer",
    company: "Microsoft",
    initials: "MP",
    linkedinUrl: "https://www.linkedin.com/in/monica-porucznik",
    photo: "/testimonials/pr/monica-porucznik.jpg",
  },
  {
    quote:
      "A good, focused session. Clear notes on how to strengthen the portfolio for senior product roles, thank you again.",
    name: "Ashwin Vasudevan",
    role: "Product and Service Designer",
    company: "Volvo Cars",
    initials: "AV",
    linkedinUrl: "https://www.linkedin.com/in/ashwinv8n",
    photo: "/testimonials/pr/ashwin-vasudevan.jpg",
  },
  {
    quote:
      "Valuable insight. The feedback was interesting and useful for how I think about my work and where to improve.",
    name: "Ann Kyrk",
    role: "Web Designer and Developer",
    initials: "AK",
    linkedinUrl: "https://www.linkedin.com/in/ann-kyrk",
    photo: "/testimonials/pr/ann-kyrk.jpg",
  },
  {
    quote:
      "A genuinely useful review with a strong foundation. The overview alone made it worth it for me.",
    name: "Ludmilla Ramos",
    role: "Product Designer",
    company: "Sparquo",
    initials: "LR",
    linkedinUrl: "https://www.linkedin.com/in/ludmillaramos/",
    photo: "/testimonials/ludmilla-ramos.jpg",
  },
];
